import { Button, Heading, Text, TextField } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useEffect, useMemo, useState } from 'react';

import { InfoRow } from '@/spaces/people/InfoRow';
import {
	isValidEmail,
	isValidInstagram,
	isValidPhone,
} from '@/utils/contactsCsv';

import { EditableCard } from '../brand/EditableCard';
import { CARD, cardTitleStyles } from '../brand/styles';

import type { Contact, ContactPatch } from './types';
import {
	formatRelativeTime,
	formatSource,
	getActiveSources,
	getEmailConsentStatus,
	getEmail,
	getInstagramHandle,
	getLastSeen,
	getPhone,
} from './utils';

interface ContactDetailProps {
	contact: Contact;
	isSaving?: boolean;
	saveError?: string | null;
	onSave?: (updates: ContactPatch) => Promise<void> | void;
	onCancel?: () => void;
	isNew?: boolean;
}

export const ContactDetail = memo(function ContactDetail({
	contact,
	isSaving = false,
	saveError = null,
	onSave,
	onCancel,
	isNew = false,
}: ContactDetailProps) {
	const email = getEmail(contact);
	const phone = getPhone(contact);
	const instagramHandle = getInstagramHandle(contact);
	const emailConsent = getEmailConsentStatus(contact);
	const activeSources = getActiveSources(contact);
	const currentDraft = useMemo(
		() => ({
			name: contact.name ?? '',
			email: contact.email ?? email ?? '',
			phone: contact.phone ?? phone ?? '',
			instagram_handle: contact.instagram_handle ?? instagramHandle ?? '',
		}),
		[
			contact.email,
			contact.instagram_handle,
			contact.name,
			contact.phone,
			email,
			instagramHandle,
			phone,
		],
	);
	const [isEditing, setIsEditing] = useState(isNew);
	const [draft, setDraft] = useState(currentDraft);

	useEffect(() => {
		setDraft(currentDraft);
		if (!isNew) setIsEditing(false);
	}, [currentDraft, isNew]);

	const isDraftEmpty =
		!draft.name.trim() &&
		!draft.email.trim() &&
		!draft.phone.trim() &&
		!draft.instagram_handle.trim();

	const emailValue = draft.email.trim();
	const phoneValue = draft.phone.trim();
	const igValue = draft.instagram_handle.trim().replace(/^@/, '');
	const isEmailValid = !emailValue || isValidEmail(emailValue);
	const isPhoneValid = !phoneValue || isValidPhone(phoneValue);
	const isInstagramValid = !igValue || isValidInstagram(igValue);
	const hasValidationError =
		!isEmailValid || !isPhoneValid || !isInstagramValid;

	const handleSave = async () => {
		const updates: ContactPatch = {};
		const nextName = draft.name.trim();
		const nextEmail = draft.email.trim();
		const nextPhone = draft.phone.trim();
		const nextInstagramHandle = draft.instagram_handle.trim();

		if (nextName !== currentDraft.name) {
			updates.name = nextName || null;
		}
		if (nextEmail !== currentDraft.email) {
			updates.email = nextEmail || null;
		}
		if (nextPhone !== currentDraft.phone) {
			updates.phone = nextPhone || null;
		}
		if (nextInstagramHandle !== currentDraft.instagram_handle) {
			updates.instagram_handle = nextInstagramHandle || null;
		}

		if (Object.keys(updates).length === 0) {
			setIsEditing(false);
			return;
		}

		try {
			await onSave?.(updates);
			setIsEditing(false);
		} catch {
			// PeopleSpace owns and displays the API error.
		}
	};

	const handleCancel = () => {
		if (isNew && onCancel) {
			onCancel();
			return;
		}
		setDraft(currentDraft);
		setIsEditing(false);
	};

	const editFields = (
		<div className="flex flex-col gap-3">
			<TextField
				label="Name"
				value={draft.name}
				onChange={(value) => setDraft((prev) => ({ ...prev, name: value }))}
			/>
			<TextField
				label="Email"
				value={draft.email}
				isInvalid={!isEmailValid}
				errorMessage="Enter a valid email address"
				onChange={(value) => setDraft((prev) => ({ ...prev, email: value }))}
			/>
			<TextField
				label="Phone"
				value={draft.phone}
				isInvalid={!isPhoneValid}
				errorMessage="Enter a valid phone number"
				onChange={(value) => setDraft((prev) => ({ ...prev, phone: value }))}
			/>
			<TextField
				label="Instagram"
				value={draft.instagram_handle}
				isInvalid={!isInstagramValid}
				errorMessage="Letters, numbers, underscores, and periods only (max 30)"
				onChange={(value) =>
					setDraft((prev) => ({ ...prev, instagram_handle: value }))
				}
			/>
			{saveError && (
				<Text
					styles={style({
						font: 'body',
						fontSize: 'body-sm',
						color: 'negative',
					})}
				>
					{saveError}
				</Text>
			)}
		</div>
	);

	const readFields = (
		<div className="flex flex-col">
			<InfoRow
				label="Name"
				value={contact.name?.trim() || '—'}
				isPlaceholder={!contact.name?.trim()}
			/>
			<InfoRow label="Email" value={email ?? '—'} isPlaceholder={!email} />
			<InfoRow label="Phone" value={phone ?? '—'} isPlaceholder={!phone} />
			<InfoRow
				label="Instagram"
				value={instagramHandle ? `@${instagramHandle}` : '—'}
				isPlaceholder={!instagramHandle}
				isLast
			/>
		</div>
	);

	const saveDisabled =
		isSaving || (isNew && isDraftEmpty) || hasValidationError;

	return (
		<div className="flex h-full min-h-0 flex-col gap-[22px] py-4">
			{isNew ? (
				<div className={`${CARD} flex flex-col gap-3`}>
					<Heading level={3} styles={cardTitleStyles}>
						Contact Info
					</Heading>
					{editFields}
					<div className="flex justify-end gap-2 pt-2">
						<Button
							variant="secondary"
							fillStyle="outline"
							isDisabled={isSaving}
							onPress={handleCancel}
						>
							Cancel
						</Button>
						<Button
							variant="accent"
							isDisabled={saveDisabled}
							onPress={handleSave}
						>
							{isSaving ? 'Creating...' : 'Create'}
						</Button>
					</div>
				</div>
			) : (
				<>
					<EditableCard
						title="Contact Info"
						isEditing={isEditing}
						onEdit={() => setIsEditing(true)}
						onSave={() => {
							if (saveDisabled) return;
							void handleSave();
						}}
						onCancel={handleCancel}
					>
						{isEditing ? editFields : readFields}
					</EditableCard>

					<div className={`${CARD} flex flex-col gap-3`}>
						<Heading level={3} styles={cardTitleStyles}>
							Activity
						</Heading>
						<div className="flex flex-col">
							<InfoRow
								label="Email consent"
								value={emailConsent === 'unknown' ? '—' : emailConsent}
								isPlaceholder={emailConsent === 'unknown'}
							/>
							<InfoRow
								label="Source"
								value={
									activeSources.length > 0
										? activeSources.join(', ')
										: formatSource(contact.source)
								}
								isPlaceholder={activeSources.length === 0 && !contact.source}
							/>
							<InfoRow
								label="Last Seen"
								value={formatRelativeTime(getLastSeen(contact))}
								isLast
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
});
